/**
 * LifePage — 生活场景决策入口页
 * 
 * 用户描述场景，AI Agent 团队生成完整生活方案
 * 场景类型：约会/亲子/聚会/出差/购物/健身/家政/休闲
 * 
 * 设计风格：暗夜蓝 + 萤火暖金，统一主布局风格
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AGENT_REGISTRY, SCENARIO_TEMPLATES, executeAgentPipeline } from '../services/simonAI';

/* ═══════════════════════════════════════════
   工具函数
   ═══════════════════════════════════════════ */

const getTimeOfDay = () => {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return '上午好';
  if (h >= 12 && h < 18) return '下午好';
  if (h >= 18 && h < 22) return '晚上好';
  return '夜深了';
};

const getGreeting = () => {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return '新的一天即将开始';
  if (h >= 12 && h < 18) return '享受午后时光';
  if (h >= 18 && h < 22) return '美好的夜晚';
  return '注意休息';
};

/* ═══════════════════════════════════════════
   子组件
   ═══════════════════════════════════════════ */

/** 场景卡片 */
function ScenarioCard({ template, onClick, index }: { template: typeof SCENARIO_TEMPLATES[0]; onClick: (t: typeof SCENARIO_TEMPLATES[0]) => void; index: number }) {
  return (
    <button
      onClick={() => onClick(template)}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
        padding: '18px 20px', borderRadius: 16,
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
        cursor: 'pointer', textAlign: 'left',
        transition: 'all 250ms var(--ease)',
        animation: `fadeUp 0.5s ${(0.1 + index * 0.06).toFixed(2)}s var(--ease) both`,
        width: '100%',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'rgba(232, 210, 138, 0.2)';
        e.currentTarget.style.background = 'rgba(232, 210, 138, 0.05)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
        e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <span style={{ fontSize: 24, marginBottom: 8 }}>{template.icon}</span>
      <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 4 }}>{template.label}</span>
      <span style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {template.input.slice(0, 50)}...
      </span>
    </button>
  );
}

/** Agent 状态指示器 */
function AgentIndicator({ agentId, status }: { agentId: string; status: 'idle' | 'running' | 'done' }) {
  const agent = AGENT_REGISTRY[agentId];
  if (!agent) return null;

  const statusColor = status === 'done' ? '#4ade80' : status === 'running' ? agent.color : 'rgba(255,255,255,0.15)';
  const statusLabel = status === 'done' ? '✓' : status === 'running' ? '⚡' : '○';

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 14px', borderRadius: 10,
        background: status === 'running' ? `${agent.color}10` : 'rgba(255,255,255,0.02)',
        border: `1px solid ${status === 'running' ? `${agent.color}30` : 'rgba(255,255,255,0.05)'}`,
        transition: 'all 300ms var(--ease)',
        animation: status === 'running' ? 'gentlePulse 1.5s ease-in-out infinite' : 'none',
      }}
    >
      <span style={{ fontSize: 16 }}>{agent.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: status === 'running' ? agent.color : 'var(--text-secondary)', fontWeight: 500 }}>
          {agent.name}
        </div>
      </div>
      <span style={{ fontSize: 14, color: statusColor, fontWeight: 600 }}>{statusLabel}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════
   主组件
   ═══════════════════════════════════════════ */

export default function LifePage() {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeCategory, setActiveCategory] = useState('全部');
  const [agentStates, setAgentStates] = useState<Record<string, 'idle' | 'running' | 'done'>>({});
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // 滚动到顶部
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!input.trim() || isProcessing) return;
    setIsProcessing(true);
    setAgentStates({});

    try {
      await executeAgentPipeline(
        input,
        (agentId) => setAgentStates(prev => ({ ...prev, [agentId]: 'running' })),
        (agentId) => setAgentStates(prev => ({ ...prev, [agentId]: 'done' })),
      );
      // 完成后跳转到方案详情页
      navigate('/life-scenario');
    } catch (err) {
      console.error('Agent pipeline error:', err);
    } finally {
      setIsProcessing(false);
    }
  }, [input, isProcessing, navigate]);

  const handleScenarioClick = (template: typeof SCENARIO_TEMPLATES[0]) => {
    setInput(template.input);
    handleSubmit();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleInputFocus = () => inputRef.current?.focus();

  const categories = ['全部', '约会', '亲子', '聚会', '出差', '购物', '健身', '家政'];

  const filteredTemplates = activeCategory === '全部'
    ? SCENARIO_TEMPLATES
    : SCENARIO_TEMPLATES.filter(t => t.label.includes(activeCategory));

  return (
    <div
      style={{
        minHeight: '100vh',
        padding: '28px 24px 60px',
        fontFamily: 'var(--font-sans)',
        color: 'var(--text-primary)',
      }}
    >
      {/* ── 页面标题区 —— */}
      <header
        style={{
          maxWidth: 780, margin: '0 auto 28px',
          animation: 'fadeUp 0.6s var(--ease) both',
        }}
      >
        <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginBottom: 8 }}>
          {getTimeOfDay()}
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 300, letterSpacing: '-0.02em', margin: '0 0 6px' }}>
          生活场景决策
        </h1>
        <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: 0, lineHeight: 1.6 }}>
          描述你的场景，AI Agent 团队为你生成完整生活方案 — 从约会规划到购物决策
        </p>
      </header>

      {/* —— 分类导航 —— */}
      <nav
        style={{
          maxWidth: 780, margin: '0 auto 24px',
          display: 'flex', gap: 6, flexWrap: 'wrap',
          animation: 'fadeUp 0.6s 0.05s var(--ease) both',
        }}
      >
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            style={{
              padding: '6px 14px', borderRadius: 20,
              background: activeCategory === cat ? 'var(--accent-soft)' : 'rgba(255,255,255,0.03)',
              border: `1px solid ${activeCategory === cat ? 'rgba(232, 210, 138, 0.2)' : 'rgba(255,255,255,0.05)'}`,
              color: activeCategory === cat ? 'var(--accent)' : 'var(--text-muted)',
              fontSize: 13,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              fontWeight: activeCategory === cat ? 500 : 400,
              transition: 'all 200ms',
            }}
          >
            {cat}
          </button>
        ))}
      </nav>

      {/* —— 主内容区 —— */}
      <div style={{ maxWidth: 780, margin: '0 auto' }}>
        {/* 输入框区域 */}
        <div
          style={{
            display: 'flex', flexDirection: 'column', gap: 16,
            animation: 'fadeUp 0.6s 0.1s var(--ease) both',
          }}
        >
          <div
            onClick={handleInputFocus}
            style={{
              display: 'flex', alignItems: 'flex-end', gap: 12,
              padding: '16px 20px', borderRadius: 16,
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              transition: 'border-color 200ms',
            }}
          >
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="描述你想做的事情，如：周六晚上和女朋友约会，预算500元..."
              disabled={isProcessing}
              rows={2}
              style={{
                flex: 1, background: 'transparent', border: 'none', outline: 'none',
                color: 'var(--text-primary)', fontSize: 14, lineHeight: 1.6,
                resize: 'none', fontFamily: 'var(--font-sans)',
              }}
            />
            <button
              onClick={handleSubmit}
              disabled={!input.trim() || isProcessing}
              style={{
                width: 40, height: 40, borderRadius: 12, border: 'none',
                background: input.trim() ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' : 'rgba(255,255,255,0.05)',
                color: '#ffffff', cursor: input.trim() ? 'pointer' : 'default',
                fontSize: 18, opacity: isProcessing ? 0.5 : input.trim() ? 1 : 0.3,
                transition: 'all 200ms', flexShrink: 0,
              }}
            >
              {isProcessing ? '⚡' : '↵'}
            </button>
          </div>

          {/* Agent 工作状态 */}
          {isProcessing && Object.keys(agentStates).length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: 8,
                padding: '16px',
                borderRadius: 16,
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.05)',
                animation: 'fadeUp 0.3s var(--ease) both',
              }}
            >
              {Object.entries(agentStates).map(([id, status]) => (
                <AgentIndicator key={id} agentId={id} status={status} />
              ))}
            </div>
          )}
        </div>

        {/* 场景模板卡片 */}
        <div style={{ marginTop: 36 }}>
          <h2 style={{ fontSize: 16, fontWeight: 400, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
            快速开始
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: 12,
            }}
          >
            {filteredTemplates.map((template, idx) => (
              <ScenarioCard
                key={template.id}
                template={template}
                onClick={handleScenarioClick}
                index={idx}
              />
            ))}
          </div>
        </div>

        {/* Agent 矩阵（底部常驻） */}
        <div
          style={{
            marginTop: 48, paddingTop: 32,
            borderTop: '1px solid rgba(255,255,255,0.05)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#34d399', animation: 'gentlePulse 2s ease-in-out infinite' }} />
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {Object.keys(AGENT_REGISTRY).length} Agents 在线
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {Object.values(AGENT_REGISTRY).map((agent) => (
              <span
                key={agent.id}
                style={{
                  padding: '5px 12px', borderRadius: 20,
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.05)',
                  fontSize: 12, color: 'var(--text-muted)',
                }}
              >
                {agent.icon} {agent.name}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* —— 全局样式（动画） —— */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes gentlePulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
